import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import PriceTimelineChart from './PriceTimelineChart';

function ReturnPill({ label, value, highlight }) {
  if (value == null) return null;
  const n = parseFloat(value);
  const colorClass = n > 0
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : n < 0
    ? 'text-red-700 bg-red-50 border-red-200'
    : 'text-gray-600 bg-gray-50 border-gray-200';

  return (
    <div className={`flex flex-col items-center px-3 py-1.5 rounded-lg border ${colorClass} ${highlight ? 'ring-1 ring-offset-1 ring-current/30' : ''}`}>
      <span className="text-[10px] font-medium uppercase tracking-wide opacity-60">{label}</span>
      <span className="text-sm font-bold">
        {n >= 0 ? '+' : ''}{n.toFixed(1)}%
      </span>
    </div>
  );
}

function DirectionBar({ bullish, bearish, neutral }) {
  const total = bullish + bearish + neutral;
  if (total === 0) return null;
  const bullPct = Math.round((bullish / total) * 100);
  const bearPct = Math.round((bearish / total) * 100);
  const neutPct = 100 - bullPct - bearPct;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex rounded-full overflow-hidden h-1.5 w-full">
        {bullPct > 0 && <div className="bg-emerald-400" style={{ width: `${bullPct}%` }} />}
        {neutPct > 0 && <div className="bg-gray-200" style={{ width: `${neutPct}%` }} />}
        {bearPct > 0 && <div className="bg-red-400" style={{ width: `${bearPct}%` }} />}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-gray-400">
        {bullish > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
            {bullish} Bullish
          </span>
        )}
        {bearish > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
            {bearish} Bearish
          </span>
        )}
        {neutral > 0 && (
          <span className="flex items-center gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />
            {neutral} Neutral
          </span>
        )}
      </div>
    </div>
  );
}

export default function CoverageSymbolRow({ item, analystName }) {
  const [expanded, setExpanded] = useState(false);

  // Inject analyst_name into predictions so PriceTimelineChart tooltip works
  const predsWithName = item.predictions.map((p) => ({ ...p, analyst_name: analystName }));

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Row header — always visible */}
      <div
        className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Symbol */}
        <div className="w-32 flex-shrink-0">
          <div className="font-bold text-gray-900 text-sm">{item.ticker}</div>
          <div className="text-xs text-gray-400 truncate">{item.name}</div>
        </div>

        {/* Direction bar */}
        <div className="flex-1 min-w-0">
          <DirectionBar
            bullish={item.bullish_count}
            bearish={item.bearish_count}
            neutral={item.neutral_count}
          />
          <div className="text-[10px] text-gray-400 mt-1">{item.prediction_count} call{item.prediction_count !== 1 ? 's' : ''}</div>
        </div>

        {/* Return pills */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <ReturnPill label="First Call" value={item.first_call_return_pct} highlight />
          <ReturnPill label="Followers" value={item.follower_return_pct} />
        </div>

        {/* Expand toggle */}
        <div className="text-gray-300 flex-shrink-0">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {/* Expanded price chart */}
      {expanded && (
        <div className="border-t border-gray-50 px-4 pb-4 pt-1">
          <PriceTimelineChart
            ticker={item.ticker}
            exchange={item.exchange}
            predictions={predsWithName}
          />
        </div>
      )}
    </div>
  );
}
